/**
 * @voltxampere/web — Import/Export orchestration (Sprint 16)
 *
 * Bridges the SPICE parser/emitter to the UI shell:
 *
 *   - `openImportDialog()` / `openExportDialog()` flip signals the
 *     dialogs subscribe to.
 *   - `parseSpiceText(src)` runs the parse + transform pipeline,
 *     returning a `ImportPreview` for the dialog preview pane.
 *   - `commitImport(preview)` swaps the active circuit via
 *     `LoadCircuitCommand` so a single Cmd+Z reverts the import.
 *   - `exportCurrentCircuit()` snapshots the circuit signal and
 *     hands it to `toSpice()` for the export dialog.
 *
 * Sprint 14 D5 disiplini: dialogs use event delegation
 * (`data-action`) so happy-dom-driven tests work without the
 * `@click` directive ceremony.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import type { Circuit } from "@voltxampere/schema";
import {
  parseSpice,
  toSpice,
  type ParseSpiceResult,
  type ToSpiceOptions,
} from "@voltxampere/spice";

import { t } from "../i18n/index.js";
import { currentCircuit } from "./circuit.js";
import { showToast } from "./toasts.js";
import { undoStack } from "./undo-stack.js";
import { LoadCircuitCommand } from "@voltxampere/ui/canvas";

/** Result shape the import dialog renders. */
export interface ImportPreview {
  readonly ok: boolean;
  readonly circuit: Circuit | null;
  readonly warnings: readonly string[];
  readonly errors: readonly { line: number; col: number; message: string }[];
}

/** Result shape the export dialog renders. */
export interface ExportPayload {
  readonly text: string;
  readonly filename: string;
}

// Modal visibility signals — dialogs subscribe and re-render.
export const importDialogOpen = signal<boolean>(false);
export const exportDialogOpen = signal<boolean>(false);

export function openImportDialog(): void {
  importDialogOpen.value = true;
}

export function closeImportDialog(): void {
  importDialogOpen.value = false;
}

export function openExportDialog(): void {
  exportDialogOpen.value = true;
}

export function closeExportDialog(): void {
  exportDialogOpen.value = false;
}

/** Run the parse + fromSpice pipeline. Pure — no side effects. */
export function parseSpiceText(source: string): ImportPreview {
  if (source.trim().length === 0) {
    return { ok: false, circuit: null, warnings: [], errors: [] };
  }
  const result: ParseSpiceResult = parseSpice(source);
  if (result.circuit === null) {
    return {
      ok: false,
      circuit: null,
      warnings: result.warnings,
      errors: result.errors,
    };
  }
  return {
    ok: true,
    circuit: result.circuit,
    warnings: result.warnings,
    errors: [],
  };
}

/**
 * Apply a parsed circuit as the active document. Goes through the
 * shared undo stack so Cmd+Z reverts the import to the previous
 * circuit (or to `null` if nothing was loaded).
 */
export function commitImport(preview: ImportPreview): boolean {
  if (!preview.ok || preview.circuit === null) return false;
  const prev = currentCircuit.value;
  // The undo stack expects a `Circuit` baseline. If the editor was
  // empty, we craft a minimal stub so the command can still snapshot
  // and undo back to it.
  const baseline: Circuit = prev ?? preview.circuit;
  const cmd = new LoadCircuitCommand(preview.circuit);
  currentCircuit.value = undoStack.execute(cmd, baseline);
  // Sprint 26 Lane 6 (R5-IA-8) — surface import success through
  // the aria-live toast region so screen-reader users learn the
  // import landed without staring at the canvas for a re-render.
  showToast({
    kind: "success",
    title: t("toast.import.success.title"),
    message: t("toast.import.success.body"),
  });
  return true;
}

/** Snapshot the active circuit and produce SPICE text + filename. */
export function exportCurrentCircuit(opts?: ToSpiceOptions): ExportPayload | null {
  const circuit = currentCircuit.value;
  if (!circuit) return null;
  const text = toSpice(circuit, opts);
  const safeName = sanitizeFilename(circuit.name.length > 0 ? circuit.name : "circuit");
  return { text, filename: `${safeName}.cir` };
}

function sanitizeFilename(raw: string): string {
  return raw
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

/** Browser-only: trigger a file download for SPICE text. */
export function downloadSpice(payload: ExportPayload): void {
  if (typeof document === "undefined") return;
  const blob = new Blob([payload.text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = payload.filename;
  a.click();
  // Defer revoke so Safari finishes the dispatch.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

/** Browser-only: copy SPICE text to the clipboard. */
export async function copySpiceToClipboard(payload: ExportPayload): Promise<boolean> {
  if (typeof navigator === "undefined") return false;
  const clipboard = (navigator as { clipboard?: Clipboard }).clipboard;
  if (!clipboard) return false;
  try {
    await clipboard.writeText(payload.text);
    return true;
  } catch {
    return false;
  }
}
